import React, { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import RNRestart from 'react-native-restart';
import Sentry from '../../utils/Sentry/Sentry';
import theme from '../../common/theme';

interface CrashScreenProps {
  resetError?: () => void;
  error?: Error;
}

const CrashScreen: React.FC<CrashScreenProps> = ({ resetError, error }) => {
  useEffect(() => {
    Sentry.withScope(scope => {
      scope.setExtra('errorMessage', error?.message ?? 'unknown');
      scope.setExtra('errorStack', error?.stack ?? 'unavailable');
      Sentry.captureMessage('Crash screen shown', {
        level: 'fatal',
        extra: {
          errorMessage: error?.message ?? 'unknown',
          errorStack: error?.stack ?? 'unavailable',
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // error is fixed at crash time — fire once on mount

  const onReload = () => {
    if (resetError) {
      resetError();
      return;
    }

    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        const desktopGo = (
          window as unknown as {
            go?: { main?: { App?: { restartApp?: () => void } } };
          }
        ).go;
        if (desktopGo?.main?.App?.restartApp) {
          desktopGo.main.App.restartApp();
          return;
        }
        window.location.reload();
      }
      return;
    }

    RNRestart.restart();
  };

  return (
    <View style={styles.screen}>
      <View style={styles.container}>
        <Text style={styles.title}>Something went wrong</Text>
        <Text style={styles.subtitle}>
          The kitchen display hit an unexpected error. Tap reload to recover.
        </Text>
        <Pressable
          testID="btn-reload"
          accessibilityRole="button"
          onPress={onReload}
          style={({ pressed }) => [
            styles.button,
            pressed ? styles.buttonPressed : null,
          ]}
        >
          <Text style={styles.buttonLabel}>Reload</Text>
        </Pressable>
      </View>
    </View>
  );
};

export default CrashScreen;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.tertiary1,
  },
  container: {
    width: 420,
    alignSelf: 'center',
    paddingVertical: 48,
    paddingHorizontal: 56,
    borderRadius: 16,
    backgroundColor: theme.colors.white,
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    lineHeight: 28,
    marginTop: 8,
    textAlign: 'center',
    color: theme.colors.black1,
    fontWeight: '600',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 16,
    lineHeight: 20,
    textAlign: 'center',
    color: theme.colors.grey6,
  },
  button: {
    marginTop: 32,
    width: 240,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: theme.colors.green,
    alignItems: 'center',
  },
  buttonPressed: {
    opacity: 0.85,
  },
  buttonLabel: {
    color: theme.colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
});
